/**
 * Vector annotation extension handler.
 *
 * Emits @virdx/wsi-viewer annotation payload as transient CBOR bytes under
 * field_key='annotation_cbor'. The API layer materializes this into
 * annotation_files and does not persist binary data in annotation_data.
 */

import { toPointArray } from "@virdx/wsi-viewer"
import { decode, encode } from "cbor-x"

import { BaseExtensionHandler } from "../_base.ts"
import type { ExtensionManifest, SerializedAnnotation, StoredAnnotation } from "../_interface.ts"
import {
  createVectorSchema,
  resolveVectorEditRegionDomain,
  updateVectorSchema,
  vectorConfigSchema,
  vectorProjectConfigSchema
} from "./schema.ts"

export class VectorHandler extends BaseExtensionHandler {
  readonly type = "vector"
  readonly manifest: ExtensionManifest = {
    type: "vector",
    label: "Vector Annotation",
    scope: "series",
    storage: "both",
    supportsLinking: false
  }

  getConfigSchema() {
    return vectorConfigSchema
  }

  getCreateSchema() {
    return createVectorSchema
  }

  override getProjectConfigSchema() {
    return vectorProjectConfigSchema
  }

  override getEditRegionDomain(templateSchema: unknown, projectConfig: unknown): string | null {
    return resolveVectorEditRegionDomain(templateSchema, projectConfig)
  }

  getUpdateSchema() {
    return updateVectorSchema
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const data = apiData as {
      label_name?: string
      label_value?: number
      annotation?: unknown
    }

    const annotationData =
      data.annotation === undefined
        ? []
        : [
            {
              field_key: "annotation_cbor",
              value_blob: encode(data.annotation)
            }
          ]

    return {
      annotation: {
        label_name: data.label_name ?? null,
        label_value: data.label_value ?? null
      },
      data: annotationData,
      files: []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): unknown {
    let annotation: unknown = null
    const blobRow = stored.data.find((row) => row.field_key === "annotation_cbor")
    if (blobRow?.value_blob) {
      try {
        const decoded = decode(blobRow.value_blob) as Record<string, unknown>
        // If CBOR stored Int32Array (new format), convert back to {x,y}[] for JSON API
        if (decoded && decoded.points instanceof Int32Array) {
          decoded.points = toPointArray(decoded.points)
          if (Array.isArray(decoded.holes)) {
            decoded.holes = (decoded.holes as Int32Array[]).map(toPointArray)
          }
        }
        annotation = decoded
      } catch {
        annotation = null
      }
    }

    return {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      label_name: stored.annotation.label_name,
      label_value: stored.annotation.label_value,
      annotation,
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }
  }

  getExportColumns(_templateSchema: unknown): string[] {
    return ["vector_count"]
  }

  toExportRow(annotations: StoredAnnotation[], _templateSchema: unknown): Record<string, string | number> {
    return { vector_count: annotations.length }
  }
}
